import { v4 } from 'uuid';
import Vue from 'vue';
import websocketService from '../../../websocketService';
import onlineService from '../../../services/onlineService';
import {
  WEBSOCKET_EVENT_ADD_SUBSCRIBER,
  WEBSOCKET_EVENT_PUBLISH_MESSAGE,
  WEBSOCKET_EVENT_REMOVE_SUBSCRIBER,
} from '../mutations/mutationTypes';

const uuidv4 = v4;

const wsLog = (...args) =>
  process.env.NODE_ENV === 'development' && console.log(...args);

function waitForSocketOpen(socket, timeoutMs) {
  return new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error('No WebSocket instance available.'));
      return;
    }

    if (socket.readyState === WebSocket.OPEN) {
      resolve();
      return;
    }

    let settled = false;

    function cleanup() {
      socket.removeEventListener('open', onOpen); // eslint-disable-line no-use-before-define
      socket.removeEventListener('close', onClose); // eslint-disable-line no-use-before-define
      window.removeEventListener('offline', onOffline); // eslint-disable-line no-use-before-define
      clearTimeout(tid); // eslint-disable-line no-use-before-define
    }

    const onOpen = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const onClose = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('WebSocket closed before becoming open.'));
    };

    const onOffline = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Offline: WebSocket send skipped.'));
    };

    const tid = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('WebSocket open timeout.'));
    }, timeoutMs);

    socket.addEventListener('open', onOpen);
    socket.addEventListener('close', onClose);
    window.addEventListener('offline', onOffline);
  });
}

export default {
  async UPDATE_WEB_SOCKET_CONNECTION_ID({ commit }, connectionId) {
    commit('SET_WEB_SOCKET_CONNECTION_ID', connectionId);
  },

  async UPDATE_WEB_SOCKET_STATUS({ commit }, status) {
    commit('SET_WEBSOCKET_STATUS', status);
  },

  async SEND_WEBSOCKET_MESSAGE({ commit, state }, payload) {
    const DEBOUNCE_DELAY_DEFAULT = 1000; // ms
    const MAX_WAIT_TIME = 60000; // ms — max time to wait for socket to open

    const { action, data, key } = payload;

    if (!action) {
      console.error('SEND_WEBSOCKET_MESSAGE: Missing "action" in payload.');
      return;
    }

    if (!data) {
      console.error('SEND_WEBSOCKET_MESSAGE: Missing "data" in payload.');
      return;
    }

    const isOnline = await onlineService.getStatus();
    if (!isOnline) {
      return;
    }

    const messageKey = key ? key + (data._id || data) : action;
    const debounceDelay = key ? DEBOUNCE_DELAY_DEFAULT : 0;

    wsLog(
      `[WS] Queuing "${action}" (key: ${messageKey}, delay: ${debounceDelay}ms)`
    );

    if (state.debounceTimers.has(messageKey)) {
      wsLog(`[WS] Debouncing "${messageKey}" — clearing previous timer`);
      clearTimeout(state.debounceTimers.get(messageKey));
    }

    const timer = setTimeout(async () => {
      wsLog(`[WS] Sending "${action}"`);
      try {
        await waitForSocketOpen(Vue.prototype.$socket, MAX_WAIT_TIME);

        const fifoMessage = {
          MessageGroupId: 'default',
          MessageDeduplicationId: uuidv4(),
          MessageBody: payload,
        };

        websocketService.sendMessage(fifoMessage);
      } catch (error) {
        if (action !== 'registerToken') {
          console.error(`[WS] Failed to send "${action}":`, error);
        }
      } finally {
        commit('REMOVE_DEBOUNCE_TIMER', messageKey);
      }
    }, debounceDelay);

    commit('SET_DEBOUNCE_TIMER', { key: messageKey, timer });
  },

  WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION({ commit }, { action, callback }) {
    commit(
      WEBSOCKET_EVENT_ADD_SUBSCRIBER,
      { action, callback },
      { root: true }
    );
  },

  WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION({ commit }, { action, callback }) {
    commit(
      WEBSOCKET_EVENT_REMOVE_SUBSCRIBER,
      { action, callback },
      { root: true }
    );
  },

  WEBSOCKET_EVENT_HANDLE_MESSAGE({ commit }, message) {
    try {
      const { action, data, user } = message;
      commit(
        WEBSOCKET_EVENT_PUBLISH_MESSAGE,
        { action, data, user },
        { root: true }
      );
    } catch (error) {
      console.error('Failed to handle WebSocket message:', error);
    }
  },
};
